import type { Node, Edge } from '@xyflow/react'
import { sortParentsFirst } from '@/lib/canvas/sort-parents-first'
import {
  normalizeEdgeForCompare,
  normalizeNodeForCompare,
} from '@/utils/canvas/canvasContentSnapshot'
import type { SavedNode } from '@/components/canvas/utils/coord-converter'
import type { SerializedEdge } from '@/lib/api/canvas-types'

/**
 * Three-way merge of canvas content after a revision conflict.
 *
 * The server rejects a save whose revision is stale rather than overwriting the
 * page, which stops one tab erasing another's work but leaves the losing tab
 * holding edits the server has never seen. Throwing them away would be the same
 * data loss one step later, so instead we merge: `base` is the content the
 * server was last known to hold, `local` is what this tab has now, `remote` is
 * what the server holds after the other tab's save.
 *
 * Per node (and per edge, by id):
 *   - untouched locally  → take the server's version, including its deletion
 *   - untouched remotely → take ours, including our deletion
 *   - added on one side  → keep it
 *   - changed on both    → keep ours and report it, because the person looking
 *                          at this tab is the one who would notice work vanish
 *
 * That last case is the only one that can lose anything, and it is the one the
 * caller surfaces to the user.
 *
 * Comparison happens on the saved shape (what `buildContentPayload` produces),
 * because that is what the server holds, and through the same normalizer the
 * autosave uses. That last part matters: React Flow writes `measured`,
 * `dragging` and `selected` onto nodes nobody edited, so comparing raw JSON
 * would call every one of them "changed here" and let a measurement outrank
 * another tab's real edit — silently losing exactly the work this file exists
 * to protect. Two tabs left open would also livelock, each re-saving its own
 * version of the noise forever.
 *
 * Where this tab wins, the live React Flow object is returned instead of its
 * saved shape, so a merge does not reset a running generation or a measured
 * size.
 */

/** Anything the merge can identify — nodes, edges, and their saved shapes */
interface Identified {
  id: string
}

/** Which side a merged item came from */
type Side = 'local' | 'remote'

/** Reduces an item to the form both sides are compared in */
type Normalizer = (item: Identified) => unknown

export interface MergeDecisions {
  /** Winning side per id; ids absent from the map were deleted */
  winner: Map<string, Side>
  /** Order the surviving ids should be emitted in */
  order: string[]
  /** Ids changed on both sides where the local version was kept */
  conflictedIds: string[]
}

/**
 * JSON with object keys in a fixed order (array order is left alone).
 *
 * The three sides of the merge reach us by different routes — one from the
 * page load, one from React Flow's own updates, one straight out of the
 * database — and none of them promises the key order the others used. Plain
 * `JSON.stringify` would read `{id, type}` and `{type, id}` as two different
 * nodes and raise a conflict over a node nobody touched. Only the merge pays
 * for this; the autosave's own comparison stays on the cheap path because a
 * false positive there costs one redundant save, not somebody's work.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`
}

/** Absent items compare equal to each other, never to anything present */
function keyOf(item: Identified | undefined, normalize: Normalizer): string | null {
  return item === undefined ? null : stableStringify(normalize(item))
}

function index<T extends Identified>(items: readonly T[]): Map<string, T> {
  const map = new Map<string, T>()
  for (const item of items) map.set(item.id, item)
  return map
}

/**
 * Decide, id by id, which side of a three-way merge wins.
 *
 * All three collections are in the saved shape, so a plain JSON comparison is
 * the right notion of "changed".
 */
function mergeDecisions<T extends Identified>(
  baseItems: readonly T[],
  localItems: readonly T[],
  remoteItems: readonly T[],
  normalize: Normalizer,
): MergeDecisions {
  const base = index(baseItems)
  const local = index(localItems)
  const remote = index(remoteItems)

  const winner = new Map<string, Side>()
  const conflictedIds: string[] = []
  const decided = new Set<string>()

  const decide = (id: string): void => {
    if (decided.has(id)) return
    decided.add(id)

    const localKey = keyOf(local.get(id), normalize)
    const remoteKey = keyOf(remote.get(id), normalize)
    if (localKey === remoteKey) {
      // Same on both sides, or gone from both — nothing to decide
      if (localKey !== null) winner.set(id, 'local')
      return
    }

    const baseKey = keyOf(base.get(id), normalize)
    const localChanged = localKey !== baseKey
    const remoteChanged = remoteKey !== baseKey

    if (!localChanged) {
      if (remoteKey !== null) winner.set(id, 'remote')
      return
    }
    if (!remoteChanged) {
      if (localKey !== null) winner.set(id, 'local')
      return
    }

    // Both sides moved: keep this tab's work and let the caller say so
    conflictedIds.push(id)
    if (localKey !== null) winner.set(id, 'local')
  }

  for (const item of remoteItems) decide(item.id)
  for (const item of localItems) decide(item.id)
  for (const item of baseItems) decide(item.id)

  // Server order first so items another tab added keep their place, then ours
  const order: string[] = []
  const emitted = new Set<string>()
  for (const item of [...remoteItems, ...localItems]) {
    if (winner.has(item.id) && !emitted.has(item.id)) {
      emitted.add(item.id)
      order.push(item.id)
    }
  }

  return { winner, order, conflictedIds }
}

export interface CanvasSavedContent {
  nodes: SavedNode[]
  edges: SerializedEdge[]
}

export interface CanvasContentMergeResult {
  nodes: Node[]
  edges: Edge[]
  /** Node and edge ids whose remote version was discarded in favour of ours */
  conflictedIds: string[]
  /** True when the merge kept anything the server does not already have */
  hasLocalChanges: boolean
  /**
   * The server's nodes that won. They carry state this tab has to adopt beyond
   * the node itself — series data lives in a separate store and has to follow.
   */
  adoptedNodes: SavedNode[]
}

export interface MergeCanvasContentParams {
  /** Content the server was known to hold when this tab last synced */
  base: CanvasSavedContent
  /** This tab's content in saved shape (what it tried to send) */
  local: CanvasSavedContent
  /** This tab's live React Flow objects, returned wherever it wins */
  localLive: { nodes: Node[]; edges: Edge[] }
  /** The server's content now */
  remote: CanvasSavedContent
}

/**
 * Assemble the merged collection, and say whether it differs from the server's.
 *
 * Where this tab wins, the live React Flow object is emitted in place of its
 * saved shape; where the server wins, its stored item is used as-is.
 */
function collect(
  decisions: MergeDecisions,
  localLive: readonly Identified[],
  localSaved: readonly Identified[],
  remoteSaved: readonly Identified[],
  normalize: Normalizer,
): { items: Identified[]; divergesFromRemote: boolean } {
  const live = index(localLive)
  const localById = index(localSaved)
  const remoteById = index(remoteSaved)

  const items: Identified[] = []
  // A deletion the server has not seen counts as divergence on its own
  let divergesFromRemote = decisions.order.length !== remoteSaved.length

  for (const id of decisions.order) {
    if (decisions.winner.get(id) === 'local') {
      const saved = localById.get(id)
      if (keyOf(saved, normalize) !== keyOf(remoteById.get(id), normalize)) divergesFromRemote = true
      // Prefer the live object so in-flight UI state survives the merge
      const item = live.get(id) ?? saved
      if (item) items.push(item)
    } else {
      const item = remoteById.get(id)
      if (item) items.push(item)
    }
  }

  return { items, divergesFromRemote }
}

/**
 * A node the merge adopts from the server goes straight into React Flow state,
 * so it has to arrive as clean as one that came through page hydration. Pages
 * stored before the loader started dropping top-level `handles` still carry it,
 * and React Flow v12 reads that as the declarative handles API — which silently
 * disables DOM-measured handle bounds and breaks every edge on the node.
 */
function stripDeclarativeHandles(node: SavedNode): SavedNode {
  if (!('handles' in node)) return node
  const { handles: _handles, ...rest } = node
  return rest as SavedNode
}

/**
 * Merge a page's nodes and edges after the server reported a newer revision.
 *
 * Nodes come back sorted parents-first, which React Flow requires for group
 * containment to render: the merge can otherwise place a node the server added
 * ahead of the parent this tab created.
 */
export function mergeCanvasContent(params: MergeCanvasContentParams): CanvasContentMergeResult {
  const { base, local, localLive } = params
  const remote = { nodes: params.remote.nodes.map(stripDeclarativeHandles), edges: params.remote.edges }

  const normalizeNode = normalizeNodeForCompare as Normalizer
  const normalizeEdge = normalizeEdgeForCompare as Normalizer

  const nodeDecisions = mergeDecisions(base.nodes, local.nodes, remote.nodes, normalizeNode)
  const edgeDecisions = mergeDecisions(base.edges, local.edges, remote.edges, normalizeEdge)

  const nodes = collect(nodeDecisions, localLive.nodes as unknown as Identified[], local.nodes, remote.nodes, normalizeNode)
  const edges = collect(edgeDecisions, localLive.edges as unknown as Identified[], local.edges, remote.edges, normalizeEdge)

  const adoptedNodes = remote.nodes.filter(node => nodeDecisions.winner.get(node.id) === 'remote')

  return {
    nodes: sortParentsFirst(nodes.items as unknown as Node[]),
    edges: edges.items as unknown as Edge[],
    conflictedIds: [...nodeDecisions.conflictedIds, ...edgeDecisions.conflictedIds],
    hasLocalChanges: nodes.divergesFromRemote || edges.divergesFromRemote,
    adoptedNodes,
  }
}
